import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import {
  getFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQById,
  setCreateFAQ,
  setUpdateFAQ,
  setDeleteFAQById,
} from '../../redux/actions/faq';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import FAQModal from './subcomponents/FAQModal';
import { hasPermission } from '../../utils/permissions';

function FAQList() {
  const dispatch = useDispatch();
  const listState = useSelector((state) => state.faq.get);
  const createState = useSelector((state) => state.faq.create);
  const updateState = useSelector((state) => state.faq.update);
  const deleteState = useSelector((state) => state.faq.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getFAQs());
  }, [dispatch]);

  const handleCreate = useCallback(
    (categoria, pregunta, respuesta) => {
      setLoading(true);
      dispatch(createFAQ(categoria, pregunta, respuesta));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, categoria, pregunta, respuesta) => {
      setLoading(true);
      dispatch(updateFAQ(id, categoria, pregunta, respuesta));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteFAQById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (faq) => {
    setEditingFAQ(faq);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingFAQ(null);
    setIsEditModalOpen(false);
  };

  const onFAQCreate = (categoria, pregunta, respuesta) => {
    handleCreate(categoria, pregunta, respuesta);
    closeCreateModal();
  };

  const onFAQEdit = (categoria, pregunta, respuesta) => {
    if (editingFAQ) {
      handleEdit(editingFAQ.id, categoria, pregunta, respuesta);
      closeEditModal();
    }
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = 'BGL - Preguntas Frecuentes';
    // Solo carga inicial
    setLoading(true);
    dispatch(getFAQs());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getFAQs());
      }
      clearReduxState(setCreateFAQ);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getFAQs());
      }
      clearReduxState(setUpdateFAQ);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getFAQs());
      }
      clearReduxState(setDeleteFAQById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('faqs.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear pregunta frecuente</span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Preguntas Frecuentes</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Categoría</th>
              <th>Pregunta</th>
              <th>Respuesta</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center">
                  No hay preguntas frecuentes disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>
                    <span
                      className={`badge ${
                        row.categoria === 'Compra'
                          ? 'badge-success'
                          : 'badge-info'
                      }`}
                    >
                      {row.categoria}
                    </span>
                  </td>
                  <td>{row.pregunta}</td>
                  <td>
                    {row.respuesta.length > 100
                      ? `${row.respuesta.substring(0, 100)}...`
                      : row.respuesta}
                  </td>
                  <td>{row.active ? 'Sí' : 'No'}</td>
                  <td>
                    <>
                      {hasPermission('faqs.edit') && (
                        <Button
                          className="mr-2"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('faqs.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger"
                          iconClass="fa fa-trash"
                          title="Eliminar pregunta frecuente"
                          message="¿Estas seguro que quieres eliminar esta pregunta frecuente? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create FAQ Modal */}
      <FAQModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear pregunta frecuente"
        onSubmit={onFAQCreate}
      />

      {/* Edit FAQ Modal */}
      <FAQModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar pregunta frecuente"
        defaultValue={editingFAQ}
        onSubmit={onFAQEdit}
      />
    </>
  );
}

export default FAQList;
